import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import './Login.css';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [token] = useState(searchParams.get('token') || '');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [userInfo, setUserInfo] = useState(null);

  useEffect(() => {
    if (!token) {
      navigate('/forgot-password');
      return;
    }

    // Verify token on component mount
    verifyToken();
  }, [token, navigate]);

  const verifyToken = async () => {
    try {
      const response = await api.post('/api/verify-reset-token', { token });
      
      if (response.data.valid) {
        setUserInfo(response.data.user);
        setVerifying(false);
      }
    } catch (error) {
      setError(error.response?.data?.error || 'Invalid or expired reset link');
      setVerifying(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      setLoading(false);
      return;
    }

    try {
      await api.post('/api/reset-password', {
        token: token,
        password: newPassword
      });

      setMessage('Password reset successful! Redirecting to login...');
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <div className="cricket-logo">
              <svg width="60" height="60" viewBox="0 0 100 100" className="cricket-icon">
                <circle cx="50" cy="50" r="45" fill="#1e40af" stroke="#ffffff" strokeWidth="3"/>
                <rect x="35" y="20" width="6" height="60" fill="#ffffff" rx="3"/>
                <rect x="55" y="20" width="6" height="60" fill="#ffffff" rx="3"/>
                <rect x="45" y="20" width="6" height="60" fill="#ffffff" rx="3"/>
                <rect x="30" y="75" width="40" height="4" fill="#ffffff" rx="2"/>
                <circle cx="50" cy="35" r="8" fill="#ef4444"/>
              </svg>
            </div>
            <h1 className="academy-title">{process.env.REACT_APP_ACADEMY_NAME || 'MSKICA Cricket Academy'}</h1>
            <p className="academy-subtitle">Verifying Reset Link...</p>
          </div>
          <div className="login-form-section">
            <div className="loading-container">
              <div className="spinner"></div>
              <p>Verifying your reset link...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !userInfo) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <div className="cricket-logo">
              <svg width="60" height="60" viewBox="0 0 100 100" className="cricket-icon">
                <circle cx="50" cy="50" r="45" fill="#ef4444" stroke="#ffffff" strokeWidth="3"/>
                <text x="50" y="60" textAnchor="middle" fill="#ffffff" fontSize="40" fontWeight="bold">!</text>
              </svg>
            </div>
            <h1 className="academy-title">{process.env.REACT_APP_ACADEMY_NAME || 'MSKICA Cricket Academy'}</h1>
            <p className="academy-subtitle">Invalid Reset Link</p>
          </div>
          <div className="login-form-section">
            <div className="error-alert">
              <span className="error-icon">⚠️</span>
              <span>{error}</span>
            </div>
            <div className="login-footer">
              <Link to="/forgot-password" className="footer-link">
                Request a new reset link? <strong>Click here</strong>
              </Link>
              <Link to="/login" className="footer-link">
                Remember your password? <strong>Sign In</strong>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-card">
        {/* Header with Cricket Logo */}
        <div className="login-header">
          <div className="cricket-logo">
            <svg width="60" height="60" viewBox="0 0 100 100" className="cricket-icon">
              <circle cx="50" cy="50" r="45" fill="#1e40af" stroke="#ffffff" strokeWidth="3"/>
              <rect x="35" y="20" width="6" height="60" fill="#ffffff" rx="3"/>
              <rect x="55" y="20" width="6" height="60" fill="#ffffff" rx="3"/>
              <rect x="45" y="20" width="6" height="60" fill="#ffffff" rx="3"/>
              <rect x="30" y="75" width="40" height="4" fill="#ffffff" rx="2"/>
              <circle cx="50" cy="35" r="8" fill="#ef4444"/>
            </svg>
          </div>
          <h1 className="academy-title">{process.env.REACT_APP_ACADEMY_NAME || 'MSKICA Cricket Academy'}</h1>
          <p className="academy-subtitle">Reset Your Password</p>
        </div>

        {/* Form Section */}
        <div className="login-form-section">
          {userInfo && (
            <div className="user-info-card">
              <p><strong>Account:</strong> {userInfo.email}</p>
              <p><strong>Name:</strong> {userInfo.first_name} {userInfo.last_name}</p>
            </div>
          )}

          {error && (
            <div className="error-alert">
              <span className="error-icon">⚠️</span>
              <span>{error}</span>
            </div>
          )}

          {message && (
            <div className="success-alert">
              <span className="success-icon">✅</span>
              <span>{message}</span>
            </div>
          )}

          <form onSubmit={handleResetPassword} className="login-form">
            <div className="form-group">
              <label htmlFor="newPassword" className="form-label">
                <span className="label-icon">🔒</span>
                New Password
              </label>
              <input
                type="password"
                id="newPassword"
                name="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="form-input"
                placeholder="Enter new password"
                minLength={6}
                required
              />
              <small className="form-help">
                Must be at least 6 characters long
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword" className="form-label">
                <span className="label-icon">🔒</span>
                Confirm Password
              </label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="form-input"
                placeholder="Confirm new password"
                minLength={6}
                required
              />
            </div>

            <button 
              type="submit" 
              className="login-btn"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="spinner"></div>
                  Resetting Password...
                </>
              ) : (
                <>
                  <span>Reset Password</span>
                  <span className="btn-icon">🔒</span>
                </>
              )}
            </button>
          </form>

          {/* Footer Links */}
          <div className="login-footer">
            <Link to="/login" className="footer-link">
              Remember your password? <strong>Sign In</strong>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
